import _ from 'underscore';
import type jquery from 'jquery';
import type {
  GlobalAppWindow,
  GlobalAppServerContext,
  OmnitureData,
  OmnitureEventPayload,
} from './types';

export type JQueryApi = typeof jquery;

const globalWindow = window as GlobalAppWindow;
let jqueryInstance: JQueryApi | null = null;
export function setJQuery(jq: JQueryApi) {
  jqueryInstance = jq;
}

// import { scopeStore } from '@bingads-webui-campaign/global-context-store';

/* Note:
 * The events we fire to adobe omniture are used by the marketing team.
 * We should not break or change the events payload without confirming the changes with them.
 * Marketing Team: Galen Gerling, David Kline
 */

function serializeList(list: Record<string, unknown>): string[] {
  return _.chain(list)
    .pairs()
    .map(item => (item[1] ? item[0] : null))
    .compact()
    .value();
}

export function triggerOmnitureEvent(payload: OmnitureEventPayload) {
  if (jqueryInstance) jqueryInstance(document).trigger('pageview', payload);
  document.dispatchEvent(new CustomEvent('bingads:pageview', { detail: payload }));
}

// The marketing team uses adobe omniture to track
export function initPDL(serverContext: GlobalAppServerContext) {
  // adobe tracking script will use window._PDL to provide info
  globalWindow._PDL = globalWindow._PDL || {};
  const omniture: Partial<OmnitureData> = serverContext.Omniture || {};

  globalWindow._PDL = globalWindow._PDL || {};
  globalWindow._PDL.market = omniture.Market;
  globalWindow._PDL.pageName = omniture.PageNamePDL;
  globalWindow._PDL.navLevel1 = omniture.NavLevel1;
  globalWindow._PDL.navLevel2 = omniture.NavLevel2;
  globalWindow._PDL.accountNumber = omniture.CurrentAccountNumber;
  globalWindow._PDL.accountMode = omniture.CurrentAccountMode || omniture.AccountMode;
  globalWindow._PDL.customerID = omniture.CustomerID;
  globalWindow._PDL.userID = omniture.UserID;
  globalWindow._PDL.serverName = omniture.ServerName;

  const permissions = serializeList(serverContext.Permissions);
  const dynamicPermissions = serializeList(serverContext.DynamicPermissions);
  const ccuiPermissions = serializeList(serverContext.CCUIPilots);

  globalWindow._PDL.permissions = _.union(
    permissions,
    dynamicPermissions,
    ccuiPermissions
  ).join(',');

  // TODO
  // scopeStore.subscribe(() => {
  //   const { campaignId, adGroupId } = scopeStore.state.scope.values;
  //   window._PDL.campaignID = campaignId;
  //   window._PDL.adGroupID = adGroupId;
  // });

  // if navLevel3 was set before init, fire an event
  if (globalWindow._PDL.navLevel3) {
    triggerOmnitureEvent({
      campaignId: globalWindow._PDL.campaignID,
      adGroupId: globalWindow._PDL.adGroupID,
      navLevel3: globalWindow._PDL.navLevel3,
      navLevel3Title: globalWindow._PDL.navLevel3Title,
    });
  }
}

export function updatePdlPage(payload: OmnitureEventPayload) {
  const {
    campaignId,
    adGroupId,
    navLevel3,
    navLevel3Title,
  } = payload;

  globalWindow._PDL = globalWindow._PDL || {};
  globalWindow._PDL.campaignID = campaignId;
  globalWindow._PDL.adGroupID = adGroupId;
  globalWindow._PDL.navLevel3 = navLevel3;
  globalWindow._PDL.navLevel3Title = navLevel3Title;

  // Includes nav + campaign elements, plus any additional custom payload
  triggerOmnitureEvent(payload);
}

export function updatePdlUser(serverContext: GlobalAppServerContext) {
  const {
    CurrentAccount,
    CurrentUser,
  } = serverContext;

  globalWindow._PDL = globalWindow._PDL || {};
  globalWindow._PDL.accountNumber = CurrentAccount.AccountNumber;
  globalWindow._PDL.accountMode = CurrentAccount.AccountMode;
  globalWindow._PDL.customerID = CurrentAccount.AdvertiserCustomerID;
  globalWindow._PDL.userID = CurrentUser.Id;
}
